import { useState, useMemo, useCallback } from 'react';
import { differenceInDays, parseISO } from 'date-fns';
import V2ThemeProvider from 'components/v2/ThemeProvider';
import NiceModal from '@ebay/nice-modal-react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import Checkbox from '@mui/material/Checkbox';
import ListItemText from '@mui/material/ListItemText';
import { useModal } from '@ebay/nice-modal-react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import Button from 'components/v2/Button';
import BaseModal from 'components/v2/Modals/BaseModal';
import InputRhf from 'components/v2/FormElements/InputRhf';
import ClientAutocomplete from 'components/v2/ClientAutocomplete';
import TickerRow from './TickerRow';
import SelectableChip from './SelectableChip';
import { SAVED_ITINERARY_TAG_OPTIONS, MONTH_NAMES } from 'itrvl-types';
import { useEdgeEnquiryZap } from 'mutations/zaps';
import EdgeAvailabilityCalendar from './EdgeAvailabilityCalendar';
import EdgeEnquiryConfirmModal from './EdgeEnquiryConfirmModal';

const schema = yup.object().shape({
  client: yup.mixed().required('Client name is required'),
  email: yup.string().email('Invalid email').required('Email is required'),
  destinations: yup.array(),
  destinationMode: yup.string(),
  otherDestination: yup.string().when(['destinations', 'destinationMode'], {
    is: (destinations, mode) => (!destinations || destinations.length === 0) && mode !== 'undecided',
    then: s => s.required('Enter a destination or select one above'),
    otherwise: s => s,
  }),
  dateMode: yup.string(),
  selectedMonths: yup.array().when('dateMode', {
    is: 'flexible',
    then: s => s.min(1, 'Select at least one month'),
    otherwise: s => s,
  }),
  duration: yup.string().required('Trip duration is required'),
});

const DESTINATIONS = [
  'Botswana',
  'Kenya',
  'Mauritius',
  'Mozambique',
  'Seychelles',
  'South Africa',
  'Tanzania',
  'Uganda',
  'Zambia',
  'Rwanda',
  'Namibia',
  'Zimbabwe',
];

const BUDGET_OPTIONS = ['Low (Under $5,000)', 'Average ($5,000 - $10,000)', 'High ($10,000 - $15,000)', 'Luxury (Over $15,000)'];

const DURATION_OPTIONS = ['Short (4-6 Nights)', 'Average (7-9 Nights)', 'Long (10-13 Nights)', 'Extended (14+ Nights)'];

const sectionSx = {
  p: 0,
  gap: 3,
  display: 'flex',
  flexDirection: 'column',
};

const sectionTitleSx = {
  color: 'text.secondary',
  fontWeight: 500,
  textTransform: 'capitalize',
};

const inputSx = {
  backgroundColor: 'common.white',
};

const chipWrapperSx = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 1,
};

const subsectionLabelSx = {
  fontWeight: 500,
  mb: 1,
  textTransform: 'capitalize',
};

const radioLabelSx = {
  '& .MuiFormControlLabel-label': { color: 'text.secondary', fontWeight: 500 },
};

const footerButtonBaseSx = {
  textTransform: 'uppercase',
  fontWeight: 500,
  fontSize: '14px',
  minWidth: 130,
  height: 48,
};

const clearButtonSx = {
  ...footerButtonBaseSx,
  borderColor: 'divider',
  color: 'text.primary',
};

const cancelButtonSx = {
  ...footerButtonBaseSx,
  backgroundColor: 'darkSand.white80',
  color: 'darkSand.black80',
  '&:hover': { backgroundColor: 'darkSand.white60' },
};

const emailFieldSx = {
  ...inputSx,
  m: 0,
  height: '100%',
  '& [class*="MuiInputBase-root"]': { height: '100%' },
  '& [class*="MuiInputBase-input"]': { height: '100%' },
};

const calendarWrapperSx = {
  maxWidth: 440,
  backgroundColor: 'stoneGrey.white40',
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: 2,
};

const calendarInnerSx = {
  borderTop: '1px solid',
  borderColor: 'divider',
  px: 3,
  pt: 3,
  pb: 2,
};

const clientDetailsRowSx = {
  flexDirection: { xs: 'column', sm: 'row' },
};

const footerRowSx = {
  width: '100%',
  flexWrap: { xs: 'wrap', sm: 'nowrap' },
  gap: { xs: 1.5, sm: 0 },
};

const FORM_ID = 'edge-enquiry-form';

export default NiceModal.create(function EdgeEnquiryModal({
  initialClient = null,
  initialEmail = '',
  initialAdults = 2,
  initialChildren = 0,
  initialChildrenAges = [],
  initialMonths = [new Date().getMonth()],
  initialYear = new Date().getFullYear(),
  initialStartDate = null,
  initialEndDate = null,
  initialNotes = '',
}) {
  const modal = useModal();
  const { mutate: sendEnquiry } = useEdgeEnquiryZap();

  const {
    handleSubmit,
    control,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      client: initialClient,
      email: initialEmail,
      destinations: [],
      destinationMode: 'specific',
      otherDestination: '',
      dateMode: 'flexible',
      selectedMonths: initialMonths,
      selectedYear: initialYear,
      startDate: initialStartDate,
      endDate: initialEndDate,
      duration: '',
      adults: initialAdults,
      children: initialChildren,
      childrenAges: initialChildrenAges,
      budget: '',
      tripTypes: [],
      notes: initialNotes,
    },
  });

  const dateMode = watch('dateMode');
  const destinationMode = watch('destinationMode');
  const adults = watch('adults');
  const children = watch('children');
  const childrenAges = watch('childrenAges');
  const startDate = watch('startDate');
  const endDate = watch('endDate');

  const computedStayNights = useMemo(() => {
    if (!startDate || !endDate) return 0;
    return differenceInDays(parseISO(endDate), parseISO(startDate));
  }, [startDate, endDate]);

  const handleDateSelect = useCallback(
    dateString => {
      if (!startDate || (startDate && endDate)) {
        setValue('startDate', dateString);
        setValue('endDate', null);
      } else if (dateString === startDate) {
        setValue('startDate', null);
        setValue('endDate', null);
      } else if (dateString < startDate) {
        setValue('endDate', startDate);
        setValue('startDate', dateString);
      } else {
        setValue('endDate', dateString);
      }
    },
    [startDate, endDate, setValue],
  );

  const handleRangeSelect = useCallback(
    (start, end) => {
      setValue('startDate', start);
      setValue('endDate', end);
    },
    [setValue],
  );

  const yearOptions = useMemo(() => {
    const now = new Date().getFullYear();
    return [now, now + 1, now + 2];
  }, []);

  const [calendarMonth, setCalendarMonth] = useState(initialMonths[0] ?? new Date().getMonth());
  const [calendarYear, setCalendarYear] = useState(initialYear);
  const calendarInitialMonth = useMemo(() => new Date(calendarYear, calendarMonth), [calendarYear, calendarMonth]);

  const handleChildrenChange = useCallback(
    newCount => {
      const count = Math.max(0, newCount);
      setValue('children', count);
      const current = watch('childrenAges');
      if (count > current.length) {
        setValue('childrenAges', [...current, ...Array.from({ length: count - current.length }, () => 12)]);
      } else {
        setValue('childrenAges', current.slice(0, count));
      }
    },
    [setValue, watch],
  );

  const handleChildAgeChange = useCallback(
    (idx, value) => {
      const numeric = value.replace(/\D/g, '');
      const current = [...watch('childrenAges')];
      current[idx] = numeric === '' ? '' : Math.min(Number(numeric), 17);
      setValue('childrenAges', current);
    },
    [setValue, watch],
  );

  const handleChildAgeBlur = useCallback(
    idx => {
      const current = [...watch('childrenAges')];
      const parsed = Number(current[idx]);
      current[idx] = parsed >= 1 && parsed <= 17 ? parsed : 12;
      setValue('childrenAges', current);
    },
    [setValue, watch],
  );

  const handleClearForm = useCallback(() => {
    reset({
      client: null,
      email: '',
      destinations: [],
      destinationMode: 'specific',
      otherDestination: '',
      dateMode: 'flexible',
      selectedMonths: [new Date().getMonth()],
      selectedYear: new Date().getFullYear(),
      startDate: null,
      endDate: null,
      duration: '',
      adults: 2,
      children: 0,
      childrenAges: [],
      budget: '',
      tripTypes: [],
      notes: '',
    });
  }, [reset]);

  const onSubmit = useCallback(
    formData => {
      sendEnquiry(formData);
      modal.hide();
      NiceModal.show(EdgeEnquiryConfirmModal);
    },
    [modal, sendEnquiry],
  );

  const footerControls = (
    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={footerRowSx}>
      <Button variant="outlined" onClick={handleClearForm} sx={clearButtonSx}>
        Clear Form
      </Button>
      <Stack direction="row" gap={1}>
        <Button onClick={modal.hide} sx={cancelButtonSx}>
          Cancel
        </Button>
        <Button variant="contained" type="submit" form={FORM_ID} sx={footerButtonBaseSx}>
          Submit
        </Button>
      </Stack>
    </Stack>
  );

  return (
    <V2ThemeProvider>
      <BaseModal
        title="Request Custom Quote"
        subtext="Share your client's travel preferences below. Wilderness will email you a custom quote within one business day."
        disableHide
        maxWidth="md"
        fullWidth
        controls={footerControls}
        paperProps={{
          sx: { maxHeight: '90vh' },
          'data-test-id': 'edge-enquiry-modal',
        }}
      >
        <form id={FORM_ID} onSubmit={handleSubmit(onSubmit)}>
          <Stack gap={3}>
            {/* Client Details */}
            <Box sx={sectionSx}>
              <Typography variant="body1" sx={sectionTitleSx}>
                Client Details
              </Typography>
              <Stack direction="row" gap={1.5} alignItems="stretch" sx={clientDetailsRowSx}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Controller
                    name="client"
                    control={control}
                    render={({ field }) => <ClientAutocomplete value={field.value} onChange={field.onChange} />}
                  />
                  {errors.client && <FormHelperText error>{errors.client.message}</FormHelperText>}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0, display: 'flex' }}>
                  <Controller
                    name="email"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        {...field}
                        label="Your Email"
                        placeholder="client@email.com"
                        size="small"
                        fullWidth
                        error={!!error}
                        helperText={error?.message}
                        sx={emailFieldSx}
                      />
                    )}
                  />
                </Box>
              </Stack>
            </Box>

            {/* Destination Details */}
            <Box sx={sectionSx}>
              <Typography variant="body1" sx={sectionTitleSx}>
                Destination Details
              </Typography>
              <Box>
                <Typography variant="subtitle2" sx={subsectionLabelSx}>
                  Preferred Destinations
                </Typography>
                <Controller
                  name="destinations"
                  control={control}
                  render={({ field }) => (
                    <Box sx={chipWrapperSx}>
                      {DESTINATIONS.map(dest => (
                        <SelectableChip
                          key={dest}
                          label={dest}
                          selected={field.value.includes(dest)}
                          onClick={() => {
                            const next = field.value.includes(dest) ? field.value.filter(d => d !== dest) : [...field.value, dest];
                            field.onChange(next);
                          }}
                        />
                      ))}
                    </Box>
                  )}
                />
              </Box>
              <Box sx={{ pt: 1 }}>
                <Controller
                  name="destinationMode"
                  control={control}
                  render={({ field }) => (
                    <RadioGroup row value={field.value} onChange={field.onChange} sx={{ gap: 3 }}>
                      <FormControlLabel value="specific" control={<Radio size="small" />} label="Other Destinations" sx={radioLabelSx} />
                      <FormControlLabel value="undecided" control={<Radio size="small" />} label="Not Decided Yet" sx={radioLabelSx} />
                    </RadioGroup>
                  )}
                />
                {destinationMode === 'specific' && (
                  <InputRhf
                    name="otherDestination"
                    control={control}
                    placeholder="Some other destination"
                    size="small"
                    sx={{ ...inputSx, mt: 1 }}
                  />
                )}
              </Box>
            </Box>

            {/* Preferred Dates */}
            <Box sx={sectionSx}>
              <Box>
                <Typography variant="body1" sx={sectionTitleSx}>
                  Preferred Dates
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.primary' }}>
                  Choose dates or a flexible window
                </Typography>
              </Box>
              <Controller
                name="dateMode"
                control={control}
                render={({ field }) => (
                  <RadioGroup row value={field.value} onChange={field.onChange} sx={{ gap: 3 }}>
                    <FormControlLabel value="flexible" control={<Radio size="small" />} label="Flexible Dates" sx={radioLabelSx} />
                    <FormControlLabel value="specific" control={<Radio size="small" />} label="Specific Dates" sx={radioLabelSx} />
                  </RadioGroup>
                )}
              />

              {dateMode === 'flexible' ? (
                <Stack gap={3}>
                  <Stack direction="row" gap={1.5} alignItems="flex-start" sx={{ flexWrap: 'wrap' }}>
                    <Box>
                      <Controller
                        name="selectedMonths"
                        control={control}
                        render={({ field }) => (
                          <Select
                            multiple
                            value={field.value}
                            onChange={field.onChange}
                            size="small"
                            error={!!errors.selectedMonths}
                            sx={{ ...inputSx, minWidth: 200 }}
                            inputProps={{ 'aria-label': 'Month' }}
                            displayEmpty
                            renderValue={sel =>
                              sel.length === 0
                                ? 'Month'
                                : [...sel]
                                    .sort((a, b) => a - b)
                                    .map(i => MONTH_NAMES[i])
                                    .join(', ')
                            }
                          >
                            {MONTH_NAMES.map((name, idx) => (
                              <MenuItem key={name} value={idx}>
                                <Checkbox checked={field.value.includes(idx)} size="small" />
                                <ListItemText primary={name} />
                              </MenuItem>
                            ))}
                          </Select>
                        )}
                      />
                      {errors.selectedMonths && <FormHelperText error>{errors.selectedMonths.message}</FormHelperText>}
                    </Box>
                    <Controller
                      name="selectedYear"
                      control={control}
                      render={({ field }) => (
                        <Select
                          value={field.value}
                          onChange={field.onChange}
                          size="small"
                          sx={{ ...inputSx, width: 200 }}
                          inputProps={{ 'aria-label': 'Year' }}
                        >
                          {yearOptions.map(year => (
                            <MenuItem key={year} value={year}>
                              {year}
                            </MenuItem>
                          ))}
                        </Select>
                      )}
                    />
                  </Stack>
                  <Box>
                    <Controller
                      name="duration"
                      control={control}
                      render={({ field, fieldState: { error } }) => (
                        <Select
                          value={field.value}
                          onChange={field.onChange}
                          size="small"
                          displayEmpty
                          error={!!error}
                          sx={{ ...inputSx, width: { xs: '100%', sm: 360 } }}
                          inputProps={{ 'aria-label': 'Trip Duration' }}
                          renderValue={v => v || 'Trip Duration'}
                        >
                          {DURATION_OPTIONS.map(opt => (
                            <MenuItem key={opt} value={opt}>
                              {opt}
                            </MenuItem>
                          ))}
                        </Select>
                      )}
                    />
                    {errors.duration && <FormHelperText error>{errors.duration.message}</FormHelperText>}
                  </Box>
                </Stack>
              ) : (
                <Stack gap={3}>
                  <Stack sx={calendarWrapperSx}>
                    <Stack direction="row" gap={1} sx={{ p: 1.5 }}>
                      <Select
                        value={calendarMonth}
                        onChange={e => setCalendarMonth(e.target.value)}
                        size="small"
                        fullWidth
                        inputProps={{ 'aria-label': 'Calendar Month' }}
                        sx={{ backgroundColor: 'background.paper' }}
                      >
                        {MONTH_NAMES.map((name, idx) => (
                          <MenuItem key={name} value={idx}>
                            {name}
                          </MenuItem>
                        ))}
                      </Select>
                      <Select
                        value={calendarYear}
                        onChange={e => setCalendarYear(e.target.value)}
                        size="small"
                        fullWidth
                        inputProps={{ 'aria-label': 'Calendar Year' }}
                        sx={{ backgroundColor: 'background.paper' }}
                      >
                        {yearOptions.map(year => (
                          <MenuItem key={year} value={year}>
                            {year}
                          </MenuItem>
                        ))}
                      </Select>
                    </Stack>
                    <Box sx={calendarInnerSx}>
                      <EdgeAvailabilityCalendar
                        selectedDate={startDate}
                        onSelectDate={handleDateSelect}
                        onRangeSelect={handleRangeSelect}
                        stayNights={computedStayNights}
                        adults={adults}
                        childrenAges={childrenAges}
                        initialMonth={calendarInitialMonth}
                        skipAvailability
                      />
                    </Box>
                  </Stack>
                  <Box>
                    <Controller
                      name="duration"
                      control={control}
                      render={({ field, fieldState: { error } }) => (
                        <Select
                          value={field.value}
                          onChange={field.onChange}
                          size="small"
                          displayEmpty
                          error={!!error}
                          sx={{ ...inputSx, width: { xs: '100%', sm: 300 } }}
                          inputProps={{ 'aria-label': 'Trip Duration' }}
                          renderValue={v => v || 'Trip Duration'}
                        >
                          {DURATION_OPTIONS.map(opt => (
                            <MenuItem key={opt} value={opt}>
                              {opt}
                            </MenuItem>
                          ))}
                        </Select>
                      )}
                    />
                    {errors.duration && <FormHelperText error>{errors.duration.message}</FormHelperText>}
                  </Box>
                </Stack>
              )}
            </Box>

            {/* Traveler Details */}
            <Box sx={sectionSx}>
              <Typography variant="body1" sx={sectionTitleSx}>
                Traveler Details
              </Typography>
              <Stack gap={2}>
                <TickerRow label="Adults" value={adults} onChange={v => setValue('adults', Math.max(1, v))} />
                <TickerRow label="Children" value={children} onChange={handleChildrenChange} />
                {childrenAges.map((age, idx) => (
                  <Stack key={idx} direction="row" alignItems="center" justifyContent="space-between">
                    <Typography variant="body1">Child {idx + 1} Age</Typography>
                    <TextField
                      value={age}
                      onChange={e => handleChildAgeChange(idx, e.target.value)}
                      onBlur={() => handleChildAgeBlur(idx)}
                      size="small"
                      placeholder="12"
                      sx={{ ...inputSx, width: 200 }}
                      inputProps={{ inputMode: 'numeric' }}
                    />
                  </Stack>
                ))}
              </Stack>
              <Controller
                name="budget"
                control={control}
                render={({ field }) => (
                  <Select
                    value={field.value}
                    onChange={field.onChange}
                    size="small"
                    displayEmpty
                    fullWidth
                    sx={inputSx}
                    inputProps={{ 'aria-label': 'Budget per person' }}
                    renderValue={v => v || 'Budget per person'}
                  >
                    {BUDGET_OPTIONS.map(opt => (
                      <MenuItem key={opt} value={opt}>
                        {opt}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
              <Box>
                <Typography variant="subtitle2" sx={subsectionLabelSx}>
                  Trip Type
                </Typography>
                <Controller
                  name="tripTypes"
                  control={control}
                  render={({ field }) => (
                    <Box sx={chipWrapperSx}>
                      {SAVED_ITINERARY_TAG_OPTIONS.map(({ label, value }) => (
                        <SelectableChip
                          key={value}
                          label={label}
                          selected={field.value.includes(value)}
                          onClick={() => {
                            const next = field.value.includes(value) ? field.value.filter(v => v !== value) : [...field.value, value];
                            field.onChange(next);
                          }}
                        />
                      ))}
                    </Box>
                  )}
                />
              </Box>
            </Box>

            {/* Other Details */}
            <Box sx={sectionSx}>
              <Typography variant="body1" sx={sectionTitleSx}>
                Other Details
              </Typography>
              <InputRhf
                name="notes"
                control={control}
                label="Additional Notes"
                placeholder="Add extra details to share with Wilderness"
                size="small"
                multiline
                minRows={4}
                sx={inputSx}
              />
            </Box>
          </Stack>
        </form>
      </BaseModal>
    </V2ThemeProvider>
  );
});
